var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { noop, constant, isFunction } from './util';
/**
 * Ensures what you give it is a function.
 * If it's not, it's converted to a function that returns what you gave it.
 *
 * @example
 * const foo = () => { ... }
 * castFunction(foo) // => foo
 * castFunction(5)   // => () => 5
 */
export var castFunction = function (x) {
    return (isFunction(x) ? x : constant(x));
};
/**
 * Creates a memoized version of `f`.
 * By default this will cache calls to `f` based on the stringified value of
 * the arguments passed. You can modify this behavior by passing in a custom
 * `keyResolver`.
 *
 * @example
 * const sum = (a, b) => a + b
 * const sumMemo = memoize(sum)
 *
 * // Stores cash based on the sum of the two values so that both a + b and
 * // b + a return the same cache value.
 * const sumMemoCustom = memoize(sum, ([a, b]) => a + b)
 */
export function memoize(f, keyResolver) {
    if (keyResolver === void 0) { keyResolver = JSON.stringify; }
    function memoized() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var key = keyResolver(args);
        var cache = memoized.cache;
        if (!cache.has(key))
            cache.set(key, f.apply(void 0, __spread(args)));
        return cache.get(key);
    }
    memoized.cache = new Map();
    return memoized;
}
/**
 * Handles memoizing promise functions and cache management
 *
 * @example
 * const fakePromiseFunc = (): Promise<string> => {return new Promise((r) => r('success'))}
 * const memoizedFunc = memoizePromise(fakePromiseFunc)
 * memoizedFunc() // --> 'success'
 * memoizedFunc() // --> 'success' cached response
 * memoizedFunc() // --> 'success' cached response
 * memoizedFunc() // --> (Eg. fetch failed) clears cache and throws
 */
export var memoizePromise = function (func, keyResolver) {
    if (keyResolver === void 0) { keyResolver = JSON.stringify; }
    var memoized = memoize(func, keyResolver);
    var wrapped = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        /**
         * Return original response unless fetch fails
         */
        return memoized.apply(void 0, __spread(args)).catch(function (e) {
            /**
             * Delete the cache
             */
            memoized.cache.delete(keyResolver(args));
            throw e;
        });
    };
    wrapped.cache = memoized.cache;
    return wrapped;
};
/**
 * Creates a function that will only be evaluated once. Returns the result from
 * the first invocation on subsequent calls.
 *
 * @example
 * const example = once(x => x)
 * example(5)     // => 5
 * example(1)     // => 5
 * example('foo') // => 5
 */
export function once(f) {
    return memoize(f, constant(0));
}
/**
 * Defers an operation to "next tick".
 * Useful for breaking out of a timer callback (like `setInterval`) to perform
 * a React state render without blocking.
 *
 * @example
 * defer(() => 5) // Promise<5>
 */
export function defer(f) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return Promise.resolve().then(function () { return f.apply(void 0, __spread(args)); });
    };
}
/**
 * Thunks a function.
 * Given some arguments, will return a function that accepts arguments and
 * returns a nullary function that invokes `f` with said arguments.
 *
 * @example
 * const constant = thunk((x) => x)
 * const five = constant(5)
 * five() // => 5
 */
export function thunk(f) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return function () { return f.apply(void 0, __spread(args)); };
    };
}
/**
 * Throttles a function so that it will only be called if it hasn't been called
 * in the last `n` milliseconds.
 */
export function throttle(n, f) {
    var last = 0;
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var now = Date.now();
        if (now - last >= n) {
            last = now;
            f.apply(void 0, __spread(args));
        }
    };
}
/**
 * Debounces a function so that it will only be called once after `delay` ms,
 * regardless of how many times it's called before delay expires.
 */
export function debounce(n, f) {
    var timeout = null;
    var wrapped = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        timeout = null;
        f.apply(void 0, __spread(args));
    };
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (timeout)
            clearTimeout(timeout);
        timeout = setTimeout.apply(void 0, __spread([wrapped, n], args));
    };
}
/**
 * Given a set of functions, will return a function that invokes the given
 * functions from right to left, passing the input from the previous to the
 * next. The initial function can accept any number of arguments, the rest have
 * a fixed arity of 1.
 *
 * @example
 * const example = compose(
 *   (y) => `${x}-baz`,
 *   (x) => `${x}-bar`,
 * )
 *
 * example('foo') // => foo-bar-baz
 */
export function compose() {
    var fs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        fs[_i] = arguments[_i];
    }
    var g = (fs.pop() || noop);
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return fs.reduceRight(function (x, f) { return f(x); }, g.apply(void 0, __spread(args)));
    };
}
/**
 * Given a set of functions, will return a function that invokes the given
 * functions from left to right, passing the input from the previous to the
 * next. The initial function can accept any number of arguments, the rest have
 * a fixed arity of 1.
 *
 * @example
 * const example = flow(
 *   (y) => `${x}-baz`,
 *   (x) => `${x}-bar`,
 * )
 *
 * example('foo') // => foo-baz-bar
 */
export function flow() {
    var fs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        fs[_i] = arguments[_i];
    }
    var g = (fs.shift() || noop);
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return fs.reduce(function (x, f) { return f(x); }, g.apply(void 0, __spread(args)));
    };
}
/**
 * Given a function `f`, returns a function that inverts its return value.
 *
 * @example
 * const t = () => true
 * const f = negate(true)
 * f() // => false
 */
export function negate(f) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return !f.apply(void 0, __spread(args));
    };
}
//# sourceMappingURL=func.js.map